import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpService } from '@core/services/http.service';
import { LocalService } from '@core/services/local.service';
import { MyApplicationsService } from '@app/pages/my-application/my-app-service.service';
import { CommonService } from '@app/shared/services/common.service';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { AssignmentSessionData, errorMessages } from '@app/shared/constants/modal.model';
import { of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs';

/**
 * Opens an application in `/pages/dynamic-routing/tab/{activeversionid}` with the same
 * session + API sequence as {@link DataGridComponent.viewApplication} (My Applications grid).
 * Use from Lookup User and any other context that has an internal application id.
 */
export interface ApplicationOpenGridRow {
  id: string | number;
  application_id?: string;
  application_type?: string;
  date_submitted?: string;
  re_submitted_date?: string;
  application_number?: string;
  educator_name?: string;
  application_status?: string;
  is_archived?: boolean;
  /** Encrypted or plain, from grid/API */
  license_number?: string;
  licenseNumber?: string;
  [key: string]: unknown;
}

/** Internal DB application id as a finite number, or null if not purely numeric. */
export function normalizeNumericApplicationId(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  const s = String(raw)
    .trim()
    .replace(/^["']+|["']+$/g, '');
  const n = Number(s);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : null;
}

/** Close / back navigation after opening an app (same shape as queue / My App session). */
export interface ApplicationOpenFromGridReturnTargets {
  backToHome: { name: string };
  backToGridPage: { name: string };
}

/** One-shot session key: consumed by Lookup User detail to restore the tab after Close from an application. */
export const LOOKUP_USER_ACTIVE_TAB_KEY = 'lookup-user-active-tab';

/** One-shot session key: stores the URL to return to when closing the educator profile (queues, complaint, etc.). */
export const LOOKUP_USER_RETURN_URL_KEY = 'lookup-user-return-url';

export interface ApplicationOpenFromGridOptions {
  isViewMode?: boolean;
  /** When set (e.g. Lookup User), overrides default educator My App return URLs. */
  returnTargets?: ApplicationOpenFromGridReturnTargets;
  /** Lookup-user tab key (e.g. personal | activities) to restore on Close from dynamic routing. */
  sourceTab?: string;
  /** personId from the lookup-user route; scopes the one-shot tab restore. */
  sourcePersonId?: string;
}

/** Session targets for Close from an app opened under `/pages/lookup-user/:personId`. */
export function lookupUserReturnTargets(personId: string | null | undefined): ApplicationOpenFromGridReturnTargets | undefined {
  const id = typeof personId === 'string' ? personId.trim() : '';
  if (!id) {
    return undefined;
  }
  return {
    backToHome: { name: `/pages/lookup-user/${id}` },
    backToGridPage: { name: `/lookup-user/${id}` },
  };
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationOpenFromGridService {
  constructor(
    private readonly myAppService: MyApplicationsService,
    private readonly router: Router,
    private readonly localService: LocalService,
    private readonly commonService: CommonService,
    private readonly httpService: HttpService
  ) {}

  /**
   * Loads person by application id, builds navigation banner, fetches app data, sets session, navigates.
   */
  openApplicationLikeDataGrid(selectedApp: ApplicationOpenGridRow, options?: ApplicationOpenFromGridOptions): void {
    const userRaw = this.localService.getItem('user');
    const user = typeof userRaw === 'string' && userRaw.trim() !== '' ? JSON.parse(userRaw) : userRaw || {};
    const userId = user?.id || user?.user_id || user?.userId;

    if (!userId) {
      console.error(errorMessages.noValidUser);
      return;
    }

    if (!selectedApp?.id && selectedApp?.id !== 0) {
      console.error('Application not found: missing id');
      return;
    }

    const numericInternalId = normalizeNumericApplicationId(selectedApp.id);
    /** Use number for APIs/session when id is numeric (avoids JSON.stringify adding extra quotes in session). */
    const idForApi = numericInternalId ?? selectedApp.id;

    if (selectedApp.is_archived === true || options?.isViewMode === true) {
      this.localService.setItem('applicationmode', JSON.stringify('view'));
    }

    if (selectedApp.application_type === 'License Extension') {
      this.localService.setItem('applicationmode', JSON.stringify('view'));
    }

    this.localService.setItem('applicationStatus', JSON.stringify(selectedApp.application_status ?? null));

    this.myAppService
      .getApplication(idForApi)
      .pipe(
        take(1),
        switchMap((personResponse: any) => {
          const personData = personResponse?.data?.[0];
          if (!personData) {
            throw new Error('No person data for application');
          }
          this.localService.setObj('personData', personData);

          if (!personData.cd_suffix) {
            return of({ personData, suffix: '' });
          }

          return this.httpService.get(API_ENDPOINTS.SUFFIX).pipe(
            take(1),
            map((suffixRes: any) => {
              const list = suffixRes?.data || [];
              const found = list.find((s: any) => String(s.key) === String(personData.cd_suffix));
              const suf = found?.value || '';
              if (suf) {
                personData.suffixValue = suf;
                this.localService.setObj('personData', personData);
              }
              const useSuffix = suf && suf !== 'None' ? suf : '';
              return { personData, suffix: useSuffix };
            }),
            catchError(() => of({ personData, suffix: '' }))
          );
        }),
        switchMap(({ personData, suffix }) => {
          let educatorName = (selectedApp.educator_name as string) || '';
          if (!educatorName && (personData.first_name || personData.last_name)) {
            educatorName = `${personData.first_name || ''} ${personData.middle_name || ''} ${personData.last_name || ''}`.trim();
          }
          if (suffix) {
            educatorName = `${educatorName} ${suffix} `.trim();
          }

          const submittedDateRaw = selectedApp.date_submitted;
          const bannerheader: Record<string, string> = {
            'Application ID': String(selectedApp.application_id ?? selectedApp.id),
            'Application Type': String(selectedApp.application_type ?? ''),
            'Educator Name': educatorName,
            'Submitted Date': submittedDateRaw
              ? this.commonService.getFormatedDate(submittedDateRaw)
              : '---'
          };

          if (selectedApp.re_submitted_date && selectedApp.re_submitted_date !== '---') {
            bannerheader['Resubmitted Date'] = this.commonService.getFormatedDate(selectedApp.re_submitted_date);
          }

          const licensePathValue = this.localService.getItem('license-path');
          if (licensePathValue != null && String(licensePathValue) !== 'null' && String(licensePathValue) !== '') {
            let licensePathLabel = '';
            switch (String(licensePathValue)) {
              case '2':
                licensePathLabel = 'Convert';
                break;
              case '1':
                licensePathLabel = 'Renewal';
                break;
              case '3':
                licensePathLabel = 'Initial';
                break;
              case '4':
                licensePathLabel = 'Reapply';
                break;
              default:
                licensePathLabel = String(licensePathValue);
            }
            bannerheader['License Path'] = licensePathLabel;
          }

          const licFromRow = this.commonService.resolveLicenseNumberForBanner(
            selectedApp as Record<string, unknown>
          );
          if (licFromRow) {
            bannerheader['License Number'] = licFromRow;
          }

          this.localService.setObj('navigationData', { bannerheader });

          return this.myAppService.getAppData(`appId=${idForApi}`).pipe(take(1));
        })
      )
      .subscribe({
        next: (appResponse: any) => {
          const row = appResponse?.data?.data?.[0];
          if (!row) {
            console.error(errorMessages.noActiveVersionId);
            return;
          }
          const { activeversionid, license_path, cd_application_type, resolve_base_application_type } = row;

          const licFromApi = this.commonService.resolveLicenseNumberForBanner(
            row as Record<string, unknown>
          );
          if (licFromApi) {
            const nav = this.localService.getObj('navigationData');
            const prev = nav && typeof nav === 'object' && nav !== null ? (nav as { bannerheader?: Record<string, string> }).bannerheader : undefined;
            const header = { ...(prev && typeof prev === 'object' ? prev : {}) } as Record<string, string>;
            if (!header['License Number']) {
              header['License Number'] = licFromApi;
              this.localService.setObj('navigationData', { bannerheader: header });
            }
          }

          if (license_path !== null && license_path !== undefined) {
            this.localService.setItem('license-application-type', String(cd_application_type));
            const cleanLicensePath =
              typeof license_path === 'string' ? license_path.replace(/"/g, '') : String(license_path);
            this.localService.setItem('license-path', cleanLicensePath);
          }
          
          // Store resolve_base_application_type for Resolve applications - fetch lookup and map key to value
          if (resolve_base_application_type !== null && resolve_base_application_type !== undefined) {
            this.httpService.get(API_ENDPOINTS.APPLICATION_TYPES_LOOKUP)
              .pipe(take(1))
              .subscribe({
                next: (lookupResponse: any) => {
                  const applicationtypes = lookupResponse?.data || [];
                  const matchedType = applicationtypes.find((type: any) => String(type.key) === String(resolve_base_application_type));
                  const resolveBaseAppTypeValue = matchedType ? matchedType.value : resolve_base_application_type;
                  this.localService.setItem('resolve_base_application_type', JSON.stringify(resolveBaseAppTypeValue));
                  console.log('[APP OPEN SERVICE] Stored resolve_base_application_type:', resolveBaseAppTypeValue);
                },
                error: () => {
                  // Fallback: store the key if lookup fails
                  this.localService.setItem('resolve_base_application_type', JSON.stringify(resolve_base_application_type));
                }
              });
          }

          if (!activeversionid) {
            console.error(errorMessages.noActiveVersionId);
            return;
          }

          const appIdForSession: string | number =
            numericInternalId !== null ? numericInternalId : idForApi;
          const youthNumberRaw =
            selectedApp.application_number ?? selectedApp.application_id ?? appIdForSession;
          const youthNumParsed =
            typeof youthNumberRaw === 'string' || typeof youthNumberRaw === 'number'
              ? normalizeNumericApplicationId(youthNumberRaw)
              : null;
          /** Numeric display id → number (JSON.stringify has no extra quotes); alphanumeric → plain string for session/URL. */
          const youthNumberForSession: string | number =
            youthNumParsed !== null
              ? youthNumParsed
              : typeof youthNumberRaw === 'string'
                ? youthNumberRaw.trim()
                : typeof youthNumberRaw === 'number'
                  ? youthNumberRaw
                  : String(appIdForSession);

          this.localService.setItem('youthID', JSON.stringify(appIdForSession));
          this.localService.setItem(
            'youthNumber',
            typeof youthNumberForSession === 'number'
              ? JSON.stringify(youthNumberForSession)
              : youthNumberForSession
          );
          this.localService.setItem('applicationID', JSON.stringify(appIdForSession));
          this.localService.setItem('isFirstSave', 'false');
          this.localService.setItem('applicationType', JSON.stringify(selectedApp.application_type));
          this.localService.setItem('currApplicationType', JSON.stringify(selectedApp.application_type));

          this.localService.setItem('parentGridPage', '');
          const backHome = options?.returnTargets?.backToHome ?? { name: '/pages/my-app' };
          const backGrid = options?.returnTargets?.backToGridPage ?? { name: '/my-app' };
          this.localService.setItem('backToHome', JSON.stringify(backHome));
          this.localService.setItem('backButtonText', 'Close');
          this.localService.setItem('backToGridPage', JSON.stringify(backGrid));

          if (options?.sourceTab && options?.sourcePersonId) {
            this.localService.setItem(
              LOOKUP_USER_ACTIVE_TAB_KEY,
              JSON.stringify({
                personId: String(options.sourcePersonId),
                tab: options.sourceTab
              })
            );
          } else {
            this.localService.removeItem(LOOKUP_USER_ACTIVE_TAB_KEY);
          }

          this.fetchAndSetApplicationFee(String(license_path));

          const sessionData: AssignmentSessionData = {
            youthID: appIdForSession,
            youthNumber: youthNumberForSession,
            applicationID: appIdForSession,
            isFirstSave: 'false',
            applicationType: selectedApp.application_type as string,
            currApplicationType: selectedApp.application_type as string,
            backToHome: backHome,
            backToGridPage: backGrid,
            applicationTotalFee: 0,
            applicationFee: 0
          };

          Object.entries(sessionData).forEach(([key, value]) => {
            if (value === undefined) {
              return;
            }
            if (key === 'youthNumber' && typeof value === 'string') {
              this.localService.setItem(key, value);
            } else {
              this.localService.setItem(key, JSON.stringify(value));
            }
          });

          this.router.navigate([`/pages/dynamic-routing/tab/${activeversionid}`]);
        },
        error: (err: unknown) => {
          console.error('Error opening application from grid:', err);
        }
      });
  }

  private fetchAndSetApplicationFee(licensePath: string): void {
    this.httpService.get('/domain/lookup?requestType=FEE_CATEGORY').pipe(take(1)).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        const feeKeyMap: Record<string, string> = { '1': '2', '2': '7', '3': '1', '4': '8' };
        const feeKey = feeKeyMap[licensePath] || '1';
        const feeRow = data.find((item: any) => String(item.key) === feeKey);
        if (feeRow) {
          const opt = typeof feeRow.optionaldata === 'string' ? JSON.parse(feeRow.optionaldata) : feeRow.optionaldata;
          const baseFee = Number(opt?.fee) || 0;
          this.localService.setItem('applicationTotalFee', JSON.stringify(baseFee));
          this.localService.setItem('applicationFee', JSON.stringify(baseFee));
        }
        const bgCheckRow = data.find((item: any) => String(item.key) === '6');
        if (bgCheckRow) {
          const bgOpt = typeof bgCheckRow.optionaldata === 'string' ? JSON.parse(bgCheckRow.optionaldata) : bgCheckRow.optionaldata;
          this.localService.setItem('backgroundCheckFee', JSON.stringify(Number(bgOpt?.fee) || 0));
        }
        const hasApprovedExtension = JSON.parse(this.localService.getItem('has_approved_extension') || 'false');
        if (licensePath === '1' && hasApprovedExtension) {
          const extRow = data.find((item: any) => String(item.key) === '4');
          if (extRow) {
            const extOpt = typeof extRow.optionaldata === 'string' ? JSON.parse(extRow.optionaldata) : extRow.optionaldata;
            this.localService.setItem('extensionFee', JSON.stringify(Number(extOpt?.fee) || 0));
          }
        }
      }
    });
  }
}
